import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';

import { faFile } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ContentSlides = ({ content = [], isCarousel = true, url, anchor = null, title = null, small = false, loading, seeAll = false }) => {
    const prevButtonRef = useRef(null);
    const nextButtonRef = useRef(null);
    const [hideNavigation, setHideNavigation] = useState(false);
    const [swiper, setSwiper] = useState(null);

    const getCurrentSlidesPerView = () => {
        const width = window.innerWidth;
        if (width >= 1024) return small ? 4.3 : 5.3;
        if (width >= 900) return small ? 3.8 : 4.5;
        if (width >= 768) return 3.5;
        return 2.3;
    };

    const checkNavigationVisibility = () => {
        if (content && Array.isArray(content)) {
            const currentSlidesPerView = getCurrentSlidesPerView();
            setHideNavigation(content.length <= Math.floor(currentSlidesPerView));
        } else {
            setHideNavigation(true);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            checkNavigationVisibility();
        };

        window.addEventListener('resize', handleResize);
        checkNavigationVisibility();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [content, small]);

    useEffect(() => {
        if (!swiper || !prevButtonRef.current || !nextButtonRef.current) return;

        swiper.params.navigation.prevEl = prevButtonRef.current;
        swiper.params.navigation.nextEl = nextButtonRef.current;
        swiper.params.navigation.disabledClass = 'opacity-30 hover:opacity-30';

        swiper.navigation.init();
        swiper.navigation.update();

        swiper.on('slideChange', () => {
            setTimeout(() => {
                swiper.navigation.update();
            }, 10);
        });
    }, [swiper, prevButtonRef.current, nextButtonRef.current]);

    if (loading) {
        return (
            <div className="my-10">
                {title && (
                    <h3
                        className={`text-2xl max-lg:mb-4 ${small ? 'xl:text-2xl lg:mb-3' : 'xl:text-4xl lg:mb-8'} text-secondary font-bold`}
                    >
                        {title}
                    </h3>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 xl:gap-10 opacity-20">
                    {[...Array(4)].map((_, index) => (
                        <div
                            key={index}
                            role="status"
                            className="flex items-center justify-center h-0 pb-[90%] md:pb-[103%] max-w-sm bg-gray-300 rounded-lg animate-pulse dark:bg-gray-700"
                        >
                            <span className="sr-only">Loading...</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!content || !Array.isArray(content) || content.length === 0) return null;

    return (
        <div className="my-10">
            {title &&
                (seeAll ? (
                    <div className={`${small ? 'mb-4' : 'mb-8'} flex justify-between items-center gap-4 md:gap-10 whitespace-nowrap`}>
                        <h3 className={`text-2xl ${small ? 'lg:text-2xl' : 'lg:text-4xl'} text-secondary font-bold`}>{title}</h3>

                        <div className="w-full border border-white opacity-20" />

                        <Link
                            to={`${url?.endsWith('/') ? url.slice(0, -1) : url || ''}${anchor ? `#${anchor}` : ''}`}
                            className="text-white font-semibold max-sm:text-xs md:tracking-widest uppercase px-3 md:px-5 py-1.5 md:py-2 border-2 border-secondary rounded-lg transition-all hover:bg-secondary"
                        >
                            Ver Tudo
                        </Link>
                    </div>
                ) : (
                    <h3
                        className={`text-2xl max-lg:mb-4 ${small ? 'xl:text-2xl lg:mb-3' : 'xl:text-4xl lg:mb-8'} text-secondary font-bold`}
                    >
                        {title}
                    </h3>
                ))}

            {isCarousel ? (
                <div className="relative">
                    <Swiper
                        spaceBetween={20}
                        slidesPerView={small ? 4.3 : 5.3}
                        modules={[Navigation]}
                        onSwiper={(swiperInstance) => {
                            setSwiper(swiperInstance);
                        }}
                        breakpoints={{
                            1024: {
                                slidesPerView: small ? 4.3 : 5.3,
                                spaceBetween: 20,
                            },
                            900: {
                                slidesPerView: small ? 3.8 : 4.5,
                                spaceBetween: 15,
                            },
                            768: {
                                slidesPerView: 3.5,
                            },
                            0: {
                                slidesPerView: 2.3,
                                spaceBetween: 12,
                            },
                        }}
                    >
                        {content.map((slide, index) => (
                            <SwiperSlide key={index}>
                                <SlideItem slide={slide} url={url} anchor={anchor} small={small} />
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <button
                        ref={prevButtonRef}
                        className="absolute -left-4 top-1/2 -translate-y-1/2 z-[1] w-8 h-8 bg-white bg-opacity-40 opacity-70 rounded-full cursor-pointer transition-all ease-out duration-200 hover:opacity-80 before:content-[''] before:absolute before:top-2.5 before:left-3 before:w-3 before:h-3 before:border-t-2 before:border-l-2 before:-rotate-45"
                        aria-label="Previous slide"
                    ></button>

                    <button
                        ref={nextButtonRef}
                        className="absolute -right-4 top-1/2 -translate-y-1/2 z-[1] w-8 h-8 bg-white bg-opacity-40 opacity-70 rounded-full cursor-pointer transition-all ease-out duration-200 hover:opacity-80 before:content-[''] before:absolute before:top-2.5 before:right-3 before:w-3 before:h-3 before:border-b-2 before:border-r-2 before:-rotate-45"
                        aria-label="Next slide"
                    ></button>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                    {content.map((slide, index) => (
                        <SlideItem key={index} slide={slide} url={url} small={small} />
                    ))}
                </div>
            )}
        </div>
    );
};

const SlideItem = ({ slide, url, anchor, small }) => (
    <Link
        to={`${
            slide.produto_slug
                ? `/${url.split('/')[0]}/${url.split('/')[1]}/${(slide.tipo_slug ? slide.tipo_slug + '/' : '') + slide.produto_slug}/${url.split('/')[2]}/${url.split('/')[3]}/${slide.id}`
                : `${url + (slide.tipo_slug ? slide.tipo_slug + '/' : '') + (slide.slug ? slide.slug : slide.id)}`
        }`}
        className="group block relative overflow-hidden rounded-xl border border-neutral-500"
    >
        {slide.preview ? (
            <img src={slide.preview} className="w-full" loading="lazy" alt={slide.nome || 'Slide preview'} />
        ) : (
            <div className="relative w-full h-0 pb-[calc(100%_*_43_/_32)] bg-white">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <span className="fa-layers fa-fw text-6xl md:text-8xl lg:text-9xl">
                        <FontAwesomeIcon icon={faFile} className="text-secondary" />
                        <span className="fa-layers-text fa-inverse text-sm md:text-2xl font-black mt-2 uppercase">{slide.extensao}</span>
                    </span>
                </div>
            </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent to-40% transition-all origin-bottom group-hover:scale-y-125" />

        <h5
            className={`absolute left-4 bottom-2 max-sm:text-sm max-sm:leading-tight min-height-[2.5em] max-xl:text-base right-4 ${small ? 'xl:bottom-6 text-lg' : 'xl:bottom-4 text-xl'} text-white font-semibold max-xl:leading-tight leading-tight`}
        >
            {slide.nome}
        </h5>

        {slide.lancamento && (
            <span className="absolute top-2 left-2 flex items-center gap-1 sm:gap-2 bg-tertiary px-2 py-1 rounded-md text-white text-xs font-semibold max-sm:tracking-tight xl:tracking-wide 2xl:tracking-widest uppercase max-sm:scale-90 origin-top-left">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14.998"
                    height="15"
                    viewBox="0 0 14.998 15"
                    className="fill-secondary max-sm:-mt-0.5"
                >
                    <path
                        d="M16.458,6.883a.751.751,0,0,0-.651-.507l-4.275-.34-1.85-4.1a.748.748,0,0,0-1.366,0l-1.85,4.1-4.275.34a.75.75,0,0,0-.464,1.285l3.159,3.08L3.768,15.579a.75.75,0,0,0,1.147.793L9,13.65l4.083,2.722a.751.751,0,0,0,1.138-.829l-1.372-4.8,3.4-3.061a.751.751,0,0,0,.208-.8Z"
                        transform="translate(-1.5 -1.498)"
                    />
                </svg>
                Lançamento
            </span>
        )}
    </Link>
);

export default ContentSlides;
